import { Row, useInfiniteScrollTable } from "@flaminem/ui-components";
import React, { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { DimensionedLayout } from "src/components/DimensionedLayout/DimensionedLayout.tsx";
import { useDimensions } from "src/components/DimensionedLayout/useDimensions.ts";
import { ModalTransition } from "src/components/Modal";
import { InfiniteScrollTable } from "src/components/Tables/InfiniteScrollTable.tsx";
import { defaultRowHeight } from "src/config/table-config.ts";
import { useDynamicViewSchema } from "src/dynamic-view-renderer/DynamicViewSchemaProvider/DynamicViewSchemaProvider";
import {
  AlertsSortingCriterion,
  SortingOrder,
} from "src/graphql/anti-fraud/__generated__/graphql.ts";
import { useSearchAlertsQuery } from "src/graphql/anti-fraud/queries/alerts/SearchAlertsQuery.ts";
import { SearchParams } from "src/graphql/anti-fraud/queries/typings.ts";
import { useSearch } from "src/hooks/useSearch.ts";
import { useSort } from "src/hooks/useSort";
import { useT } from "src/internationalization";
import { mapToApiAlertStatus, mapToApiAlertValidationDecision } from "src/mappers/alert.mappers";
import { AlertScoreDetailsModal } from "src/pages/Alerts/AlertScoreDetailsModal.tsx";
import AlertsToolbar from "src/pages/Alerts/AlertsToolbar.tsx";
import { useAlertsTableURLParams } from "src/pages/Alerts/useAlertsTableURLParams.ts";
import { useGetAlertStatusConfig } from "src/pages/Alerts/useGetAlertStatusConfig.ts";
import { useGetColumns } from "src/pages/Alerts/useGetColumns";
import { AlertStatus, AlertSummary } from "src/types/alert";

const AlertsTab: React.FC = () => {
  const params = useParams<{ code: AlertStatus }>();
  const alertStatusConfig = useGetAlertStatusConfig();
  const statusConfig = params?.code ? (alertStatusConfig[params?.code] ?? null) : null;
  const displayDecisionFilter = statusConfig?.status === "closed";

  const { pageSize, dimensions } = useDimensions();
  const t = useT();

  const {
    offset,
    index,
    searchTerm,
    sortBy,
    sortOrder,
    handleOnSearchTermChange,
    handleOnOffsetChange,
    handleOnSortChange,
    ruleIds,
    onlyUserAlerts,
    assignedTo,
    handleOnFiltersChange,
    decisions,
    caseTypeCodes,
  } = useAlertsTableURLParams();

  const [selectedAlert, setSelectedAlert] = React.useState<AlertSummary | null>();
  const { alertList, headlessWidgets, caseTypeDefinitions } = useDynamicViewSchema();
  const { columns, sortByMap, reverseSortByMap } = useGetColumns({
    onScenarioClick: setSelectedAlert,
    alertList,
    headlessWidgets,
    caseTypeDefinitions,
  });

  const mappedSortBy = React.useMemo(() => {
    if (!sortBy) return undefined;
    return sortByMap[sortBy];
  }, [sortBy, sortByMap]);

  const sortOptions = mappedSortBy ? { [mappedSortBy]: sortOrder } : undefined;
  const { getSortOptionOrder, selectedSortOption, setSortOption } =
    useSort<AlertsSortingCriterion>(sortOptions);

  const sorting: SearchParams["sorting"] = selectedSortOption
    ? [
        {
          criterion: selectedSortOption.field,
          order: selectedSortOption.order,
        },
      ]
    : [
        {
          criterion: "NbrOfIdentifiedCases",
          order: "DESC",
        },
      ];

  const { searchLoading, filteredData, searchError, refetch } = useSearch({
    searchQueryHook: useSearchAlertsQuery,
    searchParams: {
      limit: pageSize,
      offset: offset >= pageSize ? offset - 3 : offset,
      searchTerm,
      ruleIds: ruleIds,
      displayOnlyMyAlerts: onlyUserAlerts,
      alertStatus: statusConfig?.status ? mapToApiAlertStatus(statusConfig.status) : null,
      sorting: sorting,
      decisions: displayDecisionFilter ? decisions.map(mapToApiAlertValidationDecision) : [],
      caseTypeCodes: caseTypeCodes,
      assignedTo: assignedTo,
    },
  });

  const rows = filteredData?.alerts ?? [];

  const navigate = useNavigate();

  const { data, totalCount } = useInfiniteScrollTable(
    rows,
    filteredData?.pageInfo?.totalCount ?? 0,
    filteredData?.pageInfo?.limit ?? pageSize,
    filteredData?.pageInfo?.offset ?? offset
  );

  const selectedRows = useMemo(() => {
    if (index !== undefined) {
      const item = data[index];
      if (item) {
        return [item];
      }
    }
  }, [index, data]);

  const handleLoadMore = (startIndex: number, stopIndex: number) => {
    if (!searchLoading) {
      refetch({
        pagination: {
          limit: stopIndex - startIndex + 1,
          offset: startIndex,
        },
        searchString: searchTerm && searchTerm?.length > 0 ? searchTerm : null,
        ruleIds,
        displayOnlyMyAlerts: onlyUserAlerts,
        alertStatus: statusConfig?.status ? mapToApiAlertStatus(statusConfig.status) : null,
        ...(selectedSortOption && {
          sorting: [
            {
              criterion: selectedSortOption.field,
              order: selectedSortOption.order,
            },
          ],
        }),
      });
    }
  };

  return (
    <>
      <DimensionedLayout>
        {dimensions?.width && (
          <InfiniteScrollTable
            id="alerts-table"
            title={t("page.alerts.table.title")}
            description={statusConfig?.label}
            className={"flex-1"}
            totalCount={data.length}
            rowHeight={defaultRowHeight}
            fetchMore={handleLoadMore}
            highlightedRows={selectedRows}
            sort={{
              state: selectedSortOption
                ? {
                    sortBy: reverseSortByMap[selectedSortOption.field] ?? selectedSortOption.field,
                    sortDirection: getSortOptionOrder() as SortingOrder,
                  }
                : undefined,
              handler: ({ sortBy, sortDirection }) => {
                // sortBy here is the headlessWidgetCode from the column key
                const mappedCriterion = sortByMap[sortBy];
                if (mappedCriterion) {
                  setSortOption(mappedCriterion, sortDirection as SortingOrder | undefined);
                }
                // Keep the headlessWidgetCode in URL params for UI consistency
                handleOnSortChange({
                  sortBy,
                  sortOrder: sortDirection,
                });
              },
            }}
            toolbar={() => (
              <AlertsToolbar
                searchTerm={searchTerm}
                displayDecisionFilter={displayDecisionFilter}
                handleOnSearchTermChange={handleOnSearchTermChange}
                ruleIds={ruleIds}
                caseTypeCodes={caseTypeCodes}
                onlyUserAlerts={onlyUserAlerts}
                assignedTo={assignedTo}
                decisions={displayDecisionFilter ? decisions : []}
                handleOnFiltersChange={handleOnFiltersChange}
                placeholder={alertList.searchPlaceholder}
              />
            )}
            data={data}
            isLoading={searchLoading}
            columns={columns}
            pageSize={pageSize}
            errorText={searchError?.message}
            onRowClick={(item: Row<AlertSummary>, rowIndex: number) => {
              if (item.data) {
                handleOnOffsetChange(rowIndex < pageSize ? 0 : rowIndex - 2, rowIndex);
                navigate(`/alert/${item.data.id}`);
              }
            }}
            isError={searchError != null}
            scrollToIndex={totalCount && offset ? offset : -1}
          />
        )}
      </DimensionedLayout>
      <ModalTransition>
        {selectedAlert && (
          <AlertScoreDetailsModal alert={selectedAlert} onClose={() => setSelectedAlert(null)} />
        )}
      </ModalTransition>
    </>
  );
};

export default AlertsTab;
